import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { List, Star, Bookmark, X, BookOpen, ShoppingCart, Lock, Clock, Grid2x2, Brain, Briefcase, Target, Lightbulb, FlaskConical, Globe, GraduationCap, Coins, MessageCircle, Feather, Heart } from 'lucide-react';
import { useBookDetail } from '../hooks/useBookDetail';
import BookCommentsSection from '@/features/comments/components/BookCommentsSection';
import BookCommentsBottomSheet from '@/features/comments/components/BookCommentsBottomSheet';
import RelatedBooks from '../components/RelatedBooks';
import BookDetailCoverCarousel from '../components/BookDetailCoverCarousel';
import BookCharacters from '../components/BookCharacters';
import PurchaseBookDialog from '../components/PurchaseBookDialog';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { saveBook } from '@/features/library/api/library';
import { useBookCharacters } from '../hooks/useBookCharacters';
import GemIcon from '@/components/icons/GemIcon';
import { toPersianNumber } from '@/utils/persianNumber';
import { analyticsService } from '@/services/analyticsService';
import { useAuth } from '@/features/authentication/contexts/AuthContext';
import FeatureGuideOverlay from '@/components/ui/FeatureGuideOverlay';

const BOOK_DETAIL_CHARACTERS_GUIDE_STEPS = [
  {
    id: 'book-detail-characters',
    title: 'شخصیت‌های کتاب',
    description: 'شخصیت‌های اصلی کتاب رو ببین و با کلیک روی هر شخصیت می‌تونی معرفی هر شخصیت رو بخونی.',
    target: '[data-guide="book-detail-characters"]',
    // Keep the heading clear of the fixed start-learning/comments bar.
    bottomSafeArea: 96,
  },
];

const BookDetailPage: React.FC = () => {
  const { user } = useAuth();
  const chaptersRef = React.useRef<HTMLDivElement>(null);
  const { bookId: bookIdParam } = useParams<{ bookId: string }>();
  const navigate = useNavigate();
  const [currentView, setCurrentView] = useState<'detail' | 'comments'>('detail');
  const location = useLocation();
  const trackedBookViewRef = React.useRef<number | null>(null);

  const locationState = location.state as
    | {
      bookId?: number;
      source?:
      | 'category'
      | 'author'
      | 'recommended'
      | 'search'
      | 'my-books-reading'
      | 'my-books-read'
      | 'my-books-downloaded'
      | 'collection'
      | 'learning_path';
      books?: {
        book_id: number;
        title: string;
        author: string;
        cover_image_url?: string | null;
        topic?: string;
      }[];
      initialIndex?: number;
    }
    | undefined;

  // Get bookId from URL param first, then from state
  const bookIdNum = bookIdParam ? parseInt(bookIdParam, 10) : (locationState?.bookId ?? 0);

  const contextBooks = locationState?.books;

  // Always trust the URL param as the source of truth for which book is active
  const currentBookId = bookIdNum;

  // Route changes made with location state reuse the same pathname, so the
  // browser otherwise keeps the previous library page's scroll position.
  React.useLayoutEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: 'auto' });
  }, [currentBookId]);

  const queryClient = useQueryClient();

  const [isCommentsSheetOpen, setIsCommentsSheetOpen] = useState(false);
  const [isPurchaseDialogOpen, setIsPurchaseDialogOpen] = useState(false);
  const [hasOpenedComments, setHasOpenedComments] = useState(false);
  const [hasOpenedPurchaseDialog, setHasOpenedPurchaseDialog] = useState(false);

  // Fetch book details
  const initialBook = contextBooks?.find(b => b.book_id === currentBookId);
  const { data: book, isLoading: bookLoading, error: bookError, isFetching: bookFetching } = useBookDetail(currentBookId, {
    enabled: !!currentBookId,
    placeholderData: initialBook as any,
  });

  // Fetch characters to check if book is a novel
  const { data: charactersData } = useBookCharacters(currentBookId, {
    enabled: !!currentBookId,
  });

  const hasCharacters = (charactersData?.characters?.length || 0) > 0;
  const isNovel = hasCharacters;

  useEffect(() => {
    if (!book || !currentBookId || !book.topic || locationState?.source === 'learning_path') return;
    if (trackedBookViewRef.current === book.book_id) return;

    trackedBookViewRef.current = book.book_id;

    void analyticsService.logEvent('view_book_detail', {
      book_id: String(book.book_id),
      book_title: book.title,
      book_category: String(book.topic || 'unknown'),
      content_type: book.content_type || 'non_fiction',
      source: locationState?.source || 'library',
    });
  }, [book, currentBookId, locationState?.source]);

  // Show shimmer when we have placeholder but still fetching real data
  const showInfoShimmer = bookFetching && !book?.chapter_count;

  // Comments count from book data (no separate API call)
  const commentsCount = (book as any)?.total_comments ?? 0;
  const averageRating = Number((book as any)?.average_rating ?? 0);

  // Get chapter count and tags from book data
  const chapterCount = book?.chapter_count ?? 0;
  const bookTags = book?.tags ?? [];

  // Mutations for save/unsave + toast
  const [toastMsg, setToastMsg] = useState<string | null>(null);
  const [toastType, setToastType] = useState<'success' | 'error'>('success');
  const showToast = (msg: string, type: 'success' | 'error' = 'success') => {
    setToastType(type);
    setToastMsg(msg);
    window.setTimeout(() => setToastMsg(null), 3000);
  };

  const saveMut = useMutation({
    mutationFn: () => saveBook(currentBookId),
    onSuccess: (result) => {
      // Update book detail cache with new is_saved value
      queryClient.setQueryData(['book', currentBookId], (prev: any) => {
        if (!prev) return prev;
        return { ...prev, is_saved: result.is_saved };
      });
      // Invalidate library cache
      queryClient.invalidateQueries({ predicate: (q) => Array.isArray(q.queryKey) && q.queryKey[0] === 'userLibrary' });
    },
    onError: (e: any) => showToast((e as Error)?.message || 'خطا در تغییر وضعیت ذخیره', 'error'),
  });

  const toggleSave = () => {
    if (!currentBookId) return;
    if (saveMut.isPending) return;
    saveMut.mutate();
  };

  const handleBack = () => {
    if (currentView === 'comments') {
      setCurrentView('detail');
    } else {
      navigate('/library');
    }
  };

  const handleStartLearning = async () => {
    if (!currentBookId) return;
    // Direct navigation without subscription check
    navigate(`/learning/path`, { state: { bookId: currentBookId } });
  };

  const handleShowComments = () => {
    setHasOpenedComments(true);
    setIsCommentsSheetOpen(true);
  };

  // Get saved status directly from book data
  const saved = book?.is_saved ?? false;

  const iconForTopic = (name: string) => {
    const n = String(name || '').toLowerCase();
    if (n.includes('روان') || n.includes('psych')) return <Brain className="w-6 h-6" />;
    if (n.includes('کسب') || n.includes('بیز') || n.includes('business') || n.includes('مدیر') || n.includes('رهبری')) return <Briefcase className="w-6 h-6" />;
    if (n.includes('بازاری') || n.includes('مارکت') || n.includes('فروش') || n.includes('هدف')) return <Target className="w-6 h-6" />;
    if (n.includes('ایده') || n.includes('خلاق') || n.includes('نوآوری') || n.includes('innovation')) return <Lightbulb className="w-6 h-6" />;
    if (n.includes('علم') || n.includes('شیمی') || n.includes('فیزیک') || n.includes('science')) return <FlaskConical className="w-6 h-6" />;
    if (n.includes('سلامت') || n.includes('خانواده') || n.includes('روابط') || n.includes('health')) return <Heart className="w-6 h-6" />;
    if (n.includes('جهان') || n.includes('تاریخ') || n.includes('بین') || n.includes('world')) return <Globe className="w-6 h-6" />;
    if (n.includes('یادگیری') || n.includes('آموزش') || n.includes('تحصیل') || n.includes('education')) return <GraduationCap className="w-6 h-6" />;
    if (n.includes('مالی') || n.includes('اقتصاد') || n.includes('finance') || n.includes('money')) return <Coins className="w-6 h-6" />;
    if (n.includes('ارتباط') || n.includes('communication')) return <MessageCircle className="w-6 h-6" />;
    if (n.includes('داستان') || n.includes('رمان') || n.includes('fiction') || n.includes('novel')) return <Feather className="w-6 h-6" />;
    return <Grid2x2 className="w-6 h-6" />;
  };

  if (bookLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white" dir="rtl">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground font-persian">در حال بارگذاری...</p>
        </div>
      </div>
    );
  }

  if (bookError || !book) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-white" dir="rtl">
        <div className="text-center">
          <div className="text-4xl mb-4">📚</div>
          <p className="text-muted-foreground font-persian">کتاب یافت نشد</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" dir="rtl">
      {/* Conditional Header based on current view */}
      {currentView === 'comments' ? (
        /* Comments view header - only back arrow on top left */
        <div className="px-3 pt-1 mb-1">
          <button
            onClick={() => setCurrentView('detail')}
            className="p-2 bg-white rounded-lg hover:bg-gray-50 transition-colors shadow-sm border border-gray-200"
            aria-label="بازگشت به جزئیات کتاب"
            title="بازگشت به جزئیات کتاب"
          >
            <svg className="w-5 h-5 text-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
              <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
            </svg>
          </button>
        </div>
      ) : (
        /* Detail view header - Close first (right in RTL), Save second (left in RTL) */
        <div className="sticky z-50 flex items-center justify-between px-4 mb-0 bg-transparent pointer-events-none" style={{ top: 'calc(env(safe-area-inset-top) + 0.75rem)', marginTop: 'calc(env(safe-area-inset-top) + 0.4rem)' }}>
          {/* Close button */}
          <button
            onClick={handleBack}
            className="pointer-events-auto p-2 bg-white/95 rounded-lg hover:bg-gray-50 transition-all duration-200 hover:scale-110 shadow-sm border border-gray-200"
            aria-label="بستن صفحه"
            title="بستن صفحه"
          >
            <X className="w-5 h-5 text-foreground" />
          </button>

          {/* Saved / Bookmark toggle */}
          <button
            onClick={toggleSave}
            className={
              `pointer-events-auto p-2 rounded-lg transition-colors shadow-sm border ${saved
                ? 'bg-primary-50 border-primary-200 text-primary-600'
                : 'bg-white border-gray-200 text-muted-foreground hover:bg-gray-50'
              }`
            }
            aria-label={saved ? 'حذف از ذخیره‌ها' : 'ذخیره کتاب'}
            title={saved ? 'حذف از ذخیره‌ها' : 'ذخیره کتاب'}
          >
            <Bookmark className={`w-5 h-5 ${saved ? 'fill-current' : ''}`} />
          </button>
        </div>
      )}

      {currentView === 'comments' ? (
        <BookCommentsSection
          bookId={currentBookId}
        />
      ) : (
        <>
          <div className="book-detail-content-padding">
            <BookDetailCoverCarousel
              currentBook={book}
              booksFromContext={contextBooks}
              initialIndex={locationState?.initialIndex}
              source={locationState?.source}
            />

            {/* Book Title and Author */}
            <div className="text-center mb-4">
              <h1 className="text-lg md:text-2xl lg:text-3xl font-bold text-foreground mb-1 font-persian leading-tight">
                {book.title}
              </h1>
              <button
                onClick={() => navigate(`/author/${encodeURIComponent(book.author || '')}`)}
                className="text-sm md:text-base lg:text-xl text-muted-foreground font-persian hover:text-foreground transition-colors"
              >
                {book.author}</button>
            </div>

            {/* Info row: chapters, time, score, category */}
            <div className="mb-6">
              <div className="grid grid-cols-4 gap-3 max-w-sm mx-auto items-start px-1">

                {/* Chapters */}
                <div className="flex flex-col items-center">
                  <div className="w-14 h-14 rounded-xl border border-gray-200 bg-white flex items-center justify-center text-gray-700 shadow-[0_2px_8px_rgba(15,23,42,0.10)]">
                    <BookOpen className="w-6 h-6" />
                  </div>
                  {showInfoShimmer ? (
                    <div className="mt-2 w-12 h-4 bg-gray-200 rounded animate-pulse"></div>
                  ) : (
                    <span className="mt-2 text-sm font-persian text-muted-foreground text-center whitespace-nowrap">
                      {chapterCount.toLocaleString('fa-IR')} فصل
                    </span>
                  )}
                </div>

                {/* Time */}
                <div className="flex flex-col items-center">
                  <div className="w-14 h-14 rounded-xl border border-gray-200 bg-white flex items-center justify-center text-gray-700 shadow-[0_2px_8px_rgba(15,23,42,0.10)]">
                    <Clock className="w-6 h-6" />
                  </div>
                  {showInfoShimmer ? (
                    <div className="mt-2 w-14 h-4 bg-gray-200 rounded animate-pulse"></div>
                  ) : (
                    <span className="mt-2 text-sm font-persian text-muted-foreground text-center whitespace-nowrap">
                      {book.time
                        ? toPersianNumber(book.time)
                        : (chapterCount ? `${(chapterCount * 6).toLocaleString('fa-IR')} دقیقه` : '۱۲ دقیقه')}
                    </span>
                  )}
                </div>

                {/* Score */}
                <div className="flex flex-col items-center">
                  <div className="w-14 h-14 rounded-xl border border-gray-200 bg-white flex items-center justify-center text-gray-700 shadow-[0_2px_8px_rgba(15,23,42,0.10)]">
                    <Star className="w-6 h-6 text-gray-700" />
                  </div>
                  {showInfoShimmer ? (
                    <div className="mt-2 w-8 h-4 bg-gray-200 rounded animate-pulse"></div>
                  ) : (
                    <span className="mt-2 text-sm font-persian text-muted-foreground text-center whitespace-nowrap">
                      {Number(book.average_rating || 0).toLocaleString('fa-IR')}
                    </span>
                  )}
                </div>

                {/* Topic */}
                <div className="flex flex-col items-center">
                  <div className="w-14 h-14 rounded-xl border border-gray-200 bg-white flex items-center justify-center text-gray-700 shadow-[0_2px_8px_rgba(15,23,42,0.10)] hover:border-primary-300 hover:bg-primary-50/60 transition-colors cursor-pointer"
                    onClick={() => {
                      if (!book.topic) return;
                      navigate(`/library/category/${encodeURIComponent(book.topic)}`);
                    }}
                    role="button"
                    tabIndex={0}
                    aria-label={book.topic ? `مشاهده کتاب‌های دسته ${book.topic}` : undefined}
                  >
                    {iconForTopic(book.topic || '')}
                  </div>
                  {showInfoShimmer ? (
                    <div className="mt-2 w-16 h-4 bg-gray-200 rounded animate-pulse"></div>
                  ) : (
                    <span className="mt-2 mx-1 max-w-full text-sm font-persian text-muted-foreground text-center break-words leading-5">
                      {book.topic || '—'}
                    </span>
                  )}
                </div>

              </div>
              {toastMsg && (
                <div
                  className={`fixed left-1/2 -translate-x-1/2 z-50 px-4 py-2 rounded-lg shadow font-persian text-sm ${toastType === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'
                    }`}
                  style={{ top: 'calc(1rem + env(safe-area-inset-top))' }}
                >
                  {toastMsg}
                </div>
              )}
            </div>

            {/* Purchase Button - Between info boxes and tags */}
            {book.price && Number(book.price) > 0 && !(book as any).is_purchased && (
              <div className="mb-4 flex justify-center">
                <button
                  onClick={() => {
                    setHasOpenedPurchaseDialog(true);
                    setIsPurchaseDialogOpen(true);
                    void analyticsService.logEvent('book_gem_purchase_click', {
                      book_id: String(book.book_id),
                      book_title: book.title,
                      button_type: 'open_purchase_dialog',
                    });
                  }}
                  className="w-full max-w-[280px] py-3 px-6 rounded-xl bg-cyan-500 text-white font-persian font-bold text-base hover:bg-cyan-600 active:scale-[0.98] transition-all flex items-center justify-center gap-2"
                >
                  <span>خرید</span>
                  <span>{Number(book.price).toLocaleString('fa-IR')}</span>
                  <span className="relative -top-0.5">
                    <GemIcon size={26} color="white" />
                  </span>
                </button>
              </div>
            )}

            {/* Topics/Tags section - Moved above Description */}
            {(showInfoShimmer || bookTags.length > 0) && <div className="mb-3 mt-0 px-3">
              <div className="flex items-center gap-3 mb-2">
                <h3 className="text-sm md:text-base font-bold text-foreground font-persian">تگ ها</h3>
              </div>
              {showInfoShimmer ? (
                <div className="flex flex-wrap gap-3">
                  {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="px-5 py-2 bg-gray-200 rounded-lg animate-pulse">
                      <div className="w-16 h-4 bg-gray-300 rounded"></div>
                    </div>
                  ))}
                </div>
              ) : bookTags.length > 0 ? (
                <div className="overflow-x-auto scrollbar-hide">
                  <div className="flex gap-2 pb-2">
                    {bookTags.map((tag, index) => {
                      // Vibrant colors with white text
                      const vibrantColors = [
                        'bg-pink-500 hover:bg-pink-600',
                        'bg-blue-500 hover:bg-blue-600',
                        'bg-green-500 hover:bg-green-600',
                        'bg-purple-500 hover:bg-purple-600',
                        'bg-amber-500 hover:bg-amber-600',
                        'bg-orange-500 hover:bg-orange-600',
                        'bg-teal-500 hover:bg-teal-600',
                        'bg-indigo-500 hover:bg-indigo-600',
                        'bg-rose-500 hover:bg-rose-600',
                        'bg-cyan-500 hover:bg-cyan-600',
                      ];
                      const colorClass = vibrantColors[index % vibrantColors.length];
                      
                      return (
                        <button
                          key={tag.tag_id}
                          onClick={() => navigate(`/tag/${tag.tag_id}`, { state: { tagName: tag.tag_name } })}
                          className={`px-4 py-2 rounded-lg text-xs md:text-sm font-persian font-medium
                            ${colorClass} text-white whitespace-nowrap flex-shrink-0
                            transition-all duration-200 cursor-pointer shadow-sm
                            active:scale-95`}
                          aria-label={`نمایش کتاب های مرتبط با ${tag.tag_name}`}
                        >
                          # {tag.tag_name}
                        </button>
                      );
                    })}
                  </div>
                </div>
              ) : (
                <div className="bg-gray-50 rounded-xl border border-dashed border-gray-200 p-4">
                  <p className="text-gray-400 text-xs font-persian">موضوعی ثبت نشده است</p>
                </div>
              )}
            </div>}

            {/* Characters section - Only for novels */}
            <BookCharacters bookId={currentBookId} />

            {/* What's inside section */}
            <div className="mb-4 px-3">
              <h2 className="text-sm md:text-base font-bold text-foreground mb-2 font-persian">
                {isNovel ? 'داستان' : 'معرفی'}
              </h2>
              <p className="text-sm md:text-base text-gray-900 leading-9 font-persian text-justify">
                {book.description ||
                  `کاوش در تکنیک‌های ساده اما تأثیرگذار سوال‌پرسی برای انقلاب در ارتباطات، ایجاد روابط قوی‌تر، و کسب مزیت رقابتی در کسب‌وکار.`
                }
              </p>
            </div>

            {/* Chapter List section - Hidden */}
            <div className="hidden" ref={chaptersRef}></div>

            {/* Related Books Section */}
            {book.topic && (
              <RelatedBooks currentBookId={book.book_id} topic={book.topic} author={book.author} />
            )}
          </div>

          {hasCharacters && !bookLoading && user?.is_registered && !user.is_guest && (
            <FeatureGuideOverlay
              steps={BOOK_DETAIL_CHARACTERS_GUIDE_STEPS}
            />
          )}

          {/* Sticky Action Buttons */}
          <div className="fixed bottom-0 left-0 right-0 z-50 px-4 bg-gradient-to-t from-white/80 via-white/60 to-transparent backdrop-blur-sm" style={{ paddingBottom: 'calc(0.125rem + env(safe-area-inset-bottom))', paddingTop: '0.5rem' }}>
            <div className="flex items-center gap-3 w-full max-w-md mx-auto">
              {/* Single Start Learning Button */}
              <button
                onClick={handleStartLearning}
                className="flex-1 flex items-center justify-center gap-2 px-5 py-3 rounded-xl text-white font-persian font-bold text-sm md:text-base shadow-sm bg-primary-500 hover:bg-primary-600 active:scale-[0.98] transition-all"
              >
                <BookOpen className="w-5 h-5" />
                مطالعه
              </button>

              {/* Comments button */}
              <button
                onClick={handleShowComments}
                className="px-5 py-3 rounded-xl bg-white border-2 border-gray-900 text-gray-900 font-persian font-semibold text-sm md:text-base shadow-sm hover:bg-gray-50 active:scale-[0.98] transition-all"
              >
                نظرات
              </button>
            </div>
          </div>
        </>
      )}
      {(hasOpenedComments || isCommentsSheetOpen) && (
        <BookCommentsBottomSheet
          bookId={currentBookId}
          isOpen={isCommentsSheetOpen}
          onClose={() => setIsCommentsSheetOpen(false)}
        />
      )}

      {/* Purchase Dialog */}
      {(hasOpenedPurchaseDialog || isPurchaseDialogOpen) && (
        <PurchaseBookDialog
          isOpen={isPurchaseDialogOpen}
          onClose={() => setIsPurchaseDialogOpen(false)}
          book={{
            book_id: book.book_id,
            title: book.title,
            author: book.author,
            cover_image_url: book.cover_image_url,
            price: book.price,
          }}
        />
      )}
    </div>
  );
};

export default BookDetailPage;
